const LOCAL_ID_STORE = 'ccIdLocalStore';
const SESSION_ID_STORE = 'ccIdSessionStore';

type IdentityPropType = string | number | boolean;
type IdentityStore = Map<string, IdentityPropType>;

/*
What we track client-side
- utmSource
- utmMedium
- utmCampaign
- utmContent
- utmTerm
- gclid
- fbclid
- msclkid
- referringDomain
- referrer
- browser
- browserVersion
- OS
- device
- entryUrl
- channel
- timezone
- gaClientId
- gaMeasurementId
- gaSessionId

What we track server-side
- ipAddress
- city
- country
- region
- latitude
- longitude
*/

export const track = () => {
  const rootWindow = window.parent;

  try {
    trackUrlParams(rootWindow);
  } catch {
    console.error('Error tracking URL params for tracking props');
  }

  setTrackingProp('referringDomain', getReferringDomain());
  setTrackingProp('referrer', getReferrer());

  const { browser, version, os, device } = getDeviceDetails();

  setTrackingProp('browser', browser);
  setTrackingProp('browserVersion', version);
  setTrackingProp('OS', os);
  setTrackingProp('device', device);

  setTrackingProp('entryUrl', rootWindow.location.href);

  try {
    setTrackingProp('channel', getChannel());
  } catch {
    console.error('Error tracking channel for tracking props');
  }

  setTrackingProp('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

  const { gaMeasurementId, gaClientId, gaSessionId } = getGaDetails();
  if (gaMeasurementId) {
    setTrackingProp('gaMeasurementId', gaMeasurementId);
  }
  if (gaClientId) {
    setTrackingProp('gaClientId', gaClientId);
  }
  if (gaSessionId) {
    setTrackingProp('gaSessionId', gaSessionId);
  }
};

const trackUrlParams = (rootWindow: Window) => {
  const urlParams = new URLSearchParams(rootWindow.location.search);

  const utmSource = urlParams.get('utm_source');
  if (utmSource) {
    setTrackingProp('utmSource', utmSource);
  }

  const utmMedium = urlParams.get('utm_medium');
  if (utmMedium) {
    setTrackingProp('utmMedium', utmMedium);
  }

  const utmCampaign = urlParams.get('utm_campaign');
  if (utmCampaign) {
    setTrackingProp('utmCampaign', utmCampaign);
  }

  const utmContent = urlParams.get('utm_content');
  if (utmContent) {
    setTrackingProp('utmContent', utmContent);
  }

  const utmTerm = urlParams.get('utm_term');
  if (utmTerm) {
    setTrackingProp('utmTerm', utmTerm);
  }

  const gclid = urlParams.get('gclid');
  if (gclid) {
    setTrackingProp('gclid', gclid);
  }

  const fbclid = urlParams.get('fbclid');
  if (fbclid) {
    setTrackingProp('fbclid', fbclid);
  }

  const msclkid = urlParams.get('msclkid');
  if (msclkid) {
    setTrackingProp('msclkid', msclkid);
  }
};

const getReferrer = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const refParam = urlParams.get('ref');

  if (refParam === 'wix_editor') {
    return 'wix-editor';
  }

  if (!document.referrer) return '$direct';

  const referrer = new URL(document.referrer);

  return referrer.href;
};

const getReferringDomain = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const refParam = urlParams.get('ref');

  if (refParam === 'wix_editor') {
    return 'wix.com';
  }

  if (!document.referrer) return '$unknown';
  const referrer = new URL(document.referrer);
  const current = new URL(window.location.href);

  if (referrer.hostname === current.hostname) return '$direct';

  return referrer.hostname;
};

const getDeviceDetails = () => {
  const userAgent = navigator.userAgent;
  let browser = 'Unknown';
  let version = 'Unknown';

  // Detect browser and version (Desktop & Mobile versions)
  if (/EdgA\/(\d+)/.test(userAgent)) {
    browser = 'Edge Mobile';
    version = userAgent.match(/EdgA\/(\d+)/)?.[1] || 'Unknown';
  } else if (/Edg\/(\d+)/.test(userAgent)) {
    browser = 'Edge';
    version = userAgent.match(/Edg\/(\d+)/)?.[1] || 'Unknown';
  } else if (/CriOS\/(\d+)/.test(userAgent)) {
    browser = 'Chrome Mobile';
    version = userAgent.match(/CriOS\/(\d+)/)?.[1] || 'Unknown';
  } else if (/Chrome\/(\d+)/.test(userAgent)) {
    browser = 'Chrome';
    version = userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
  } else if (/FxiOS\/(\d+)/.test(userAgent)) {
    browser = 'Firefox Mobile';
    version = userAgent.match(/FxiOS\/(\d+)/)?.[1] || 'Unknown';
  } else if (/Firefox\/(\d+)/.test(userAgent)) {
    browser = 'Firefox';
    version = userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
  } else if (
    /Safari\/(\d+)/.test(userAgent) &&
    /Mobile/.test(userAgent) &&
    !/Chrome/.test(userAgent)
  ) {
    browser = 'Safari Mobile';
    version = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown';
  } else if (/Safari\/(\d+)/.test(userAgent) && !/Chrome/.test(userAgent)) {
    browser = 'Safari';
    version = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown';
  } else if (/MSIE (\d+)/.test(userAgent) || /Trident\/.*rv:(\d+)/.test(userAgent)) {
    browser = 'Internet Explorer';
    version =
      userAgent.match(/MSIE (\d+)/)?.[1] ||
      userAgent.match(/rv:(\d+)/)?.[1] ||
      'Unknown';
  } else if (/Opera|OPR\/(\d+)/.test(userAgent)) {
    browser = 'Opera';
    version = userAgent.match(/OPR\/(\d+)/)?.[1] || 'Unknown';
  }

  // Detect OS
  let os = 'Unknown';
  if (/windows nt 10/i.test(userAgent)) os = 'Windows 10';
  else if (/windows nt 6.3/i.test(userAgent)) os = 'Windows 8.1';
  else if (/windows nt 6.2/i.test(userAgent)) os = 'Windows 8';
  else if (/windows nt 6.1/i.test(userAgent)) os = 'Windows 7';
  else if (/windows nt 6.0/i.test(userAgent)) os = 'Windows Vista';
  else if (/windows nt 5.1|windows xp/i.test(userAgent)) os = 'Windows XP';
  else if (/macintosh|mac os x/i.test(userAgent)) os = 'MacOS';
  else if (/android/i.test(userAgent)) os = 'Android';
  else if (/linux/i.test(userAgent)) os = 'Linux';
  else if (/iphone|ipad|ipod/i.test(userAgent)) os = 'iOS';

  // Detect device type
  let device = 'Desktop';
  if (/tablet|ipad/i.test(userAgent)) device = 'Tablet';
  else if (/mobi|android|iphone|ipod/i.test(userAgent)) device = 'Mobile';

  return { browser, version, os, device };
};

const getChannel = () => {
  const rootWindow = window.parent;

  const urlParams = new URLSearchParams(rootWindow.location.search);

  const utmSource = urlParams.get('utm_source') || '';
  const utmMedium = urlParams.get('utm_medium') || '';
  const gclid = urlParams.get('gclid');
  const msclkid = urlParams.get('msclkid');
  const referringDomain = getReferringDomain();

  const socialPlatforms = [
    'facebook',
    'instagram',
    'twitter',
    'tiktok',
    'linkedin',
    'snapchat',
    'pinterest',
    'reddit',
    'threads',
    'mastodon',
    'bluesky',
    'wechat',
    'qq',
    'discord',
    'telegram',
    'whatsapp',
    'signal',
    'kakaotalk',
    'line',
    'vkontakte',
    'douyin',
    'tumblr',
    'clubhouse',
    'mix',
    'medium',
    'substack',
    'weibo',
    'flickr',
  ];

  const isSocialPlatform = socialPlatforms.some((platform) =>
    referringDomain.includes(platform),
  );

  const searchEngines = [
    'google',
    'bing',
    'yandex',
    'yahoo',
    'duckduckgo',
    'baidu',
    'ecosia',
    'aol',
    'internet archive',
    'swisscows',
    'sogou',
    'haosou',
    'shenma',
    'seva',
    'startpage',
    'yippy',
    'dogpile',
    'seekr',
    'wolframalpha',
    'mojeek',
    'naver',
    'perplexity',
    'you',
    'yep',
    'openverse',
    'gibiru',
    'brave',
    'metacrawler',
    'qwant',
    'cốc cốc',
  ];

  const isSearchEngine = searchEngines.some((engine) =>
    referringDomain.includes(engine),
  );

  const videoPlatforms = [
    'youtube',
    'twitch',
    'vimeo',
    'dailymotion',
    'bilibili',
    'rumble',
    'odysee',
    'peertube',
    'kick',
    '9gag',
    'liveleak',
    'metacafe',
    'brightcove',
    'vidme',
  ];

  const isVideoPlatform = videoPlatforms.some((platform) =>
    referringDomain.includes(platform),
  );

  if (utmMedium === 'affiliate') {
    return 'affiliate';
  }

  if (utmMedium === 'audio') {
    return 'audio';
  }

  if (utmMedium === 'cross-network') {
    return 'cross-network';
  }

  const displays = ['display', 'banner', 'expandable', 'interstitial', 'cpm'];

  if (displays.includes(utmMedium)) {
    return 'display';
  }

  const emailSources = ['gmail', 'email', 'e-mail', 'e_mail', 'e mail'];
  const emailMediums = ['email', 'e-mail', 'e_mail', 'e mail'];

  if (emailSources.includes(utmSource) || emailMediums.includes(utmMedium)) {
    return 'email';
  }

  if (
    utmSource === 'firebase' ||
    utmMedium === 'mobile' ||
    utmMedium === 'notification' ||
    utmMedium.endsWith('push')
  ) {
    return 'push';
  }

  const hasPaidMedium = /^(.*cp.*|ppc|retargeting|paid.*)$/.test(utmMedium);

  if (hasPaidMedium) {
    if (isSearchEngine) {
      return 'paid_search';
    }

    if (utmSource.includes('google') && gclid) {
      return 'paid_search';
    }

    if (utmSource.includes('bing') && msclkid) {
      return 'paid_search';
    }

    if (utmSource === 'adwords') {
      return 'paid_search';
    }

    if (isSocialPlatform) {
      return 'paid_social';
    }

    if (isVideoPlatform) {
      return 'paid_video';
    }

    return 'paid_other';
  }

  if (utmMedium === 'organic' || isSearchEngine) {
    return 'organic_search';
  }

  const socialMediaMediums = [
    'social',
    'social-network',
    'social-media',
    'sm',
    'social network',
    'social media',
  ];

  if (isSocialPlatform || socialMediaMediums.includes(utmMedium)) {
    return 'organic_social';
  }

  if (isVideoPlatform || /^(.*video.*)$/.test(utmMedium)) {
    return 'organic_video';
  }

  if (utmMedium === 'referral' || utmMedium === 'app' || utmMedium === 'link') {
    return 'referral';
  }

  if (utmSource === 'sms' || utmMedium === 'sms') {
    return 'sms';
  }

  return 'direct';
};

function getGaDetails() {
  const cookies = document.cookie.split('; ');

  // Extract client ID from _ga cookie
  const clientMatch = cookies.find((row) => row.startsWith('_ga='));
  const clientId = clientMatch ? clientMatch.split('.').slice(2).join('.') : null;

  // Extract session ID from _ga_XXXXXXXXXX cookie
  const sessionMatch = cookies.find((row) => row.startsWith('_ga_'));
  const sessionId = sessionMatch ? sessionMatch.split('.').pop() : null;

  // Extract GA measurement ID from window.gtagDataLayer (if available)
  const measurementId = window.ga ? window.ga.getAll()?.[0]?.get('trackingId') : null;

  return {
    gaMeasurementId: measurementId,
    gaClientId: clientId,
    gaSessionId: sessionId,
  };
}

const setTrackingProp = (key: string, value: IdentityPropType) => {
  const existsInSessionStore = getStorageItem(SESSION_ID_STORE, key);

  if (existsInSessionStore == null) {
    setStorageItem(SESSION_ID_STORE, key, value);
  }
};

// const setTrackingProp = (key: string, value: IdentityPropType) => {
//   const firstTouchKey = `${key}FirstTouch`;
//   const lastTouchKey = `${key}LastTouch`;

//   const existingFirstTouchInLocalStore = getStorageItem(LOCAL_ID_STORE, firstTouchKey);

//   // We only set the "first touch" variant if it doesn't exist in local storage
//   if (existingFirstTouchInLocalStore == null) {
//     setStorageItem(LOCAL_ID_STORE, firstTouchKey, value);
//   }

//   const existingInSessionStore = getStorageItem(SESSION_ID_STORE, key);

//   // We only set the "last touch" variant if it doesn't exist in session sto  rage
//   if (existingInSessionStore == null) {
//     setStorageItem(LOCAL_ID_STORE, lastTouchKey, value);
//   }

//   // We always update the "last touch" variant in session storage
//   setStorageItem(SESSION_ID_STORE, key, value);
// };

const getStorage = (storageKey: string): IdentityStore => {
  try {
    const parsed = new Map(JSON.parse(window.localStorage.getItem(storageKey) || '')) as
      | IdentityStore
      | undefined
      | null;

    return parsed || new Map();
  } catch (err) {
    return new Map();
  }
};

const clearStorage = (storageKey: string) => {
  try {
    window.localStorage.removeItem(storageKey);
  } catch (err) {
    // No op
  }
};

if (typeof window !== 'undefined') {
  // @ts-ignore
  window.getTrackingProps = () => {
    return getStorage(SESSION_ID_STORE);
  };
}

const setStorage = (storageKey: string, newStorage: IdentityStore) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(Array.from(newStorage)));

    const event = new CustomEvent('storageset', {
      detail: { storage: newStorage },
    });
    window.dispatchEvent(event);

    return newStorage;
  } catch (err) {
    if (process.env.APP_ENV === 'development') {
      console.error('Error saving identity', err);
    }

    return undefined;
  }
};

export const getStorageItem = (storageKey: string, key: string) => {
  const ccStorage = getStorage(storageKey);

  return ccStorage.get(key);
};

export const unsetStorageItem = (storageKey: string, key: string) => {
  const ccStorage = getStorage(storageKey);

  ccStorage.delete(key);

  setStorage(storageKey, ccStorage);
};

export const resetStorage = (storageKey: string) => {
  return setStorage(storageKey, new Map());
};

export const setStorageItem = (
  storageKey: string,
  key: string,
  value: IdentityPropType,
) => {
  if (value == null) return;

  const ccStorage = getStorage(storageKey);

  ccStorage.set(key, value);

  setStorage(storageKey, ccStorage);
};
